<script setup lang="ts">
import DocumentUploadButton from './DocumentUploadButton.vue';
import type { DocumentUploadProps } from './types';

/**
 * Multi-document upload component.
 *
 * Wrapper around DocumentUploadButton with multiple=true.
 * Kept as separate component for backwards compatibility with existing code.
 *
 * This component ensures that the modelValue is always an array and provides
 * a simpler API for components that only need multiple file uploads.
 */
interface MultidocumentUploadButtonProps extends Partial<DocumentUploadProps> {
  warehouseId?: string;
  storageEndpoint?: string;
  modelValue?: string[];
}

const props = withDefaults(defineProps<MultidocumentUploadButtonProps>(), {
  warehouseId: '',
  storageEndpoint: '',
  modelValue: () => [],
  label: 'Upload files',
  placeholder: 'Choose files...',
  maxFiles: null,
  s3BaseUrl: undefined,
  required: false,
  maxSize: null,
  conversionUnit: 'KB',
  allowedMimeTypes: undefined,
  rules: () => [],
  readOnly: false,
  variant: 'default',
  description: ''
});

const emit = defineEmits<{
  'update:model-value': [value: string[]];
  error: [error: Error];
  uploading: [];
  uploaded: [];
  cleared: [];
}>();

function handleUpdate(value: string | string[]) {
  // Ensure we always emit an array
  const arrayValue = Array.isArray(value) ? value : value ? [value] : [];
  emit('update:model-value', arrayValue);
}

function handleError(error: Error) {
  emit('error', error);
}

function handleUploading() {
  emit('uploading');
}

function handleUploaded() {
  emit('uploaded');
}

function handleCleared() {
  emit('cleared');
}
</script>

<template>
  <div class="multidocument-upload-button">
    <!-- This is a convenience wrapper around DocumentUploadButton with multiple=true -->
    <document-upload-button
      :warehouse-id="warehouseId"
      :storage-endpoint="storageEndpoint"
      :model-value="modelValue"
      :max-files="maxFiles"
      :s3-base-url="s3BaseUrl"
      :required="required"
      :max-size="maxSize"
      :conversion-unit="conversionUnit"
      :allowed-mime-types="allowedMimeTypes"
      :rules="rules"
      :read-only="readOnly"
      :variant="variant"
      :placeholder="placeholder"
      :label="label"
      :description="description"
      :multiple="true"
      @update:model-value="handleUpdate"
      @error="handleError"
      @uploading="handleUploading"
      @uploaded="handleUploaded"
      @cleared="handleCleared" />
  </div>
</template>

<style scoped lang="scss">
.multidocument-upload-button {
  width: 100%;
}
</style>
